

import { Dropdown, Menu, Modal, Tooltip } from "antd";
import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ProfileView } from "../header/style";
import { FindMenus } from "./menus";
import { MenuView } from "./style";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { DefaultAdminPage, DefaultUserPage } from "../../../config";
import ReportIssueModal from "../../../components/report-issue/ReportIssueModal";
import UserList from "../../user/user-list";
import DropdownUserList from "../../user/dropdown-user-list";
import { useThemePreference } from "../../../hooks/useThemePreference";

const ProfileMenu = ({ user, onLogout, setData }) => {
    const { email, name, seller_name, photo } = user?.user?.user_data || {};
    const [isModalVisible, setIsModalVisible] = useState(false);

    const showLogoutModal = () => setIsModalVisible(true);
    const handleConfirmation = () => {
        setIsModalVisible(false);
        onLogout();
    };
    const handleCancel = () => setIsModalVisible(false);

    return (
        <ProfileView
            className="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-800 menu-state-bg menu-state-color fw-semibold py-4 fs-6 w-275px"
            data-kt-menu="true"
        >
            <div className="menu-item px-3">
                <div className="menu-content d-flex align-items-center px-3">
                    <div className="symbol symbol-35px symbol-circle me-4">
                        <img alt="Profile" src={photo || "../assets/media/avatars/blank.png"} />
                    </div>
                    <div className="d-flex flex-column" style={{ wordBreak: "break-word" }}>
                        <div className="fw-bold d-flex align-items-center fs-6">{seller_name || name}</div>
                        <a href="#" className="fw-semibold text-muted text-hover-danger fs-8">
                            {email}
                        </a>
                    </div>
                </div>
            </div>
            <div className="separator my-2" />

            {user?.user?.user_data?.user_type !== 1 && (
                <>
                    {/* <div className="separator my-2" /> */}
                    <DropdownUserList userData={user} setData={setData} />
                    <div className="separator my-2" />
                </>
            )}
       

            <div className="menu-item px-5">
                <div
                    onClick={showLogoutModal}
                    className="menu-link btn btn-danger text-light rounded-5 px-5 justify-content-center profile-signout-btn"
                    style={{ cursor: "pointer" }}
                >
                    Sign Out
                </div>
            </div>
            <Modal
                title={
                    <div className="d-flex align-items-center gap-2">
                        <ExclamationCircleOutlined style={{ color: "#f45b68", fontSize: 24 }} />
                        <span className="fw-semibold fs-4 text-danger">Confirm Logout</span>
                    </div>
                }
                open={isModalVisible}
                onOk={handleConfirmation}
                onCancel={handleCancel}
                okText="Yes, Logout"
                cancelText="Cancel"
                centered
                className="custom-logout-modal"
            >
                <p className="fs-5 text-muted mb-0">Are you sure you want to log out of your session?</p>
            </Modal>
        </ProfileView>
    );
};

export default function Sidebar({
                                    collapsed,
                                    user,
                                    setData,
                                    type,
                                    toggleSidebar,
                                    location,
                                    handleLogout,
                                    bannerHeight = 0,
                                }) {
    const [current, setCurrent] = useState(location?.pathname?.replace("/", ""));
    const [openKeys, setOpenKeys] = useState([]);
    const [currentTime, setCurrentTime] = useState("");
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [reportIssueOpen, setReportIssueOpen] = useState(false);

    const [isDrpListCollapsed, setIsDrpListCollapsed] = useState(true); // true = collapsed, false = exp

    const navigate = useNavigate();

    const { themeMode, toggleTheme } = useThemePreference(user, setData);
    const isDark = themeMode === "dark";

    // On Mobile (< 992px), sidebar always looks "expanded" when open (drawer mode)
    const isMobile = windowWidth < 992;

    // Expanded only based on collapsed state — hover has no effect
    const isExpanded = isMobile ? true : !collapsed;

    const findRoutes = {
        superAdmin: DefaultAdminPage,
        admin: DefaultAdminPage,
        user: DefaultUserPage,
    };

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const path = location?.pathname || "";
        const currentKey = path.replace("/", "");
        setCurrent(currentKey);

        // Only auto-open a parent submenu if the current path is actually
        // a child of one of the parent menu items. This prevents standalone
        // routes that share a path prefix (e.g. "settings/products") from
        // expanding an unrelated parent group (e.g. "Configurations").
        const items = FindMenus(type, user) || [];
        const parentWithChild = items.find(
            (item) =>
                Array.isArray(item?.children) &&
                item.children.some((child) => child?.key === currentKey)
        );

        if (parentWithChild) {
            setOpenKeys([parentWithChild.key]);
        } else {
            setOpenKeys([]);
        }
    }, [location, type, user]);

    useEffect(() => {
        const updateTime = () => {
            const date = new Date();
            const formattedDate = `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()} ${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")} (IST)`;
            setCurrentTime(formattedDate);
        };
        updateTime();
        const intervalId = setInterval(updateTime, 1000);
        return () => clearInterval(intervalId);
    }, []);

    const menuItems = FindMenus(type, user);
    const { email, name, seller_name, photo } = user?.user?.user_data || {};
    const asanaIssueFormUrl = "https://form.asana.com/?k=6r4M2u9PDk6_tPuM0TO8iQ&d=1202787517148468";

    // ADMIN BUTTON condition (like in footer):
    const showAdminButton = !!(user?.admin && user?.user?.user_data?.parent_user_id == null);

    // Height logic for #kt_app_sidebar_navs_wrappers (key code change as per requirement)
    // If admin button is shown: height should be 'calc(100vh - 183px)'
    // If not: height should be 'calc(100vh - 90px)'
    const navsWrapperHeight = useMemo(() => {
        if (showAdminButton) {
            return "calc(100vh - 183px)";
        }
        return "calc(100vh - 133px)";
    }, [showAdminButton]);

    const sidebarLogoSrc = useMemo(() => {
        if (!isExpanded) {
            return "/assets/media/sellerView-favicon.png";
        }
        return themeMode === "dark"
            ? "/assets/media/SellerViewLight.png"
            : "/assets/media/SellerViewLogo.png";
    }, [isExpanded, themeMode]);

    return (
        <div
            id="kt_app_sidebar"
            className={`app-sidebar flex-column ${isMobile ? "drawer drawer-start" : ""}`}
            // No onMouseEnter / onMouseLeave — hover must not expand/collapse the sidebar
            style={
                bannerHeight > 0 && !isMobile
                    ? { paddingTop: bannerHeight }
                    : undefined
            }
        >
            {/* Sidebar Header */}
            <div
                className="app-sidebar-header d-flex align-items-center pt-8 pb-2 px-10"
                id="kt_app_sidebar_header"
                style={{ position: "relative" }}
            >
                <div className="app-sidebar-logo d-flex align-items-center">
                    <img
                        key={sidebarLogoSrc}
                        src={sidebarLogoSrc}
                        alt="SellerView"
                        className=""
                        style={{
                            width: isExpanded ? "150px" : "30px",
                            objectFit: "contain",
                            transition: "width 0.3s ease",
                        }}
                    />
                </div>

                {/* Toggle Button — only on desktop */}
                {!isMobile && (
                    <div
                        id="kt_app_sidebar_toggle"
                        onClick={toggleSidebar}
                        className="app-sidebar-toggle btn btn-sm btn-icon bg-body btn-color-gray-600 btn-active-color-danger border shadow-sm rotate"
                        style={{
                            position: "absolute",
                            right: "-12px",
                            top: "45px",
                            width: "24px",
                            height: "24px",
                            borderRadius: "50%",
                            cursor: "pointer",
                            zIndex: 100,
                        }}
                    >
                        <i className={`ki-outline ki-left ${collapsed ? "rotate-180" : ""} fs-8 transition-all`} />
                    </div>
                )}
            </div>

            {/* Sidebar Navs */}
            <div className="app-sidebar-navs flex-column-fluid py-6" id="kt_app_sidebar_navs">
                <div
                    id="kt_app_sidebar_navs_wrappers"
                    className={`app-sidebar-wrapper app-sidebar-scroll-wrap hover-scroll-y my-2${isMobile ? " app-sidebar-scroll-wrap--mobile" : ""}`}
                    data-kt-scroll={isMobile ? "false" : "true"}
                    data-kt-scroll-activate={isMobile ? "false" : "true"}
                    data-kt-scroll-height="auto"
                    data-kt-scroll-dependencies="#kt_app_sidebar_header"
                    data-kt-scroll-wrappers="#kt_app_sidebar_navs"
                    data-kt-scroll-offset="5px"
                    style={isMobile ? undefined : { height: "calc(100vh - 145px)" }}
                >
           
                    {/* User Info Dropdown */}
                    <div className="app-sidebar-menu-secondary menu menu-rounded menu-column mb-0">
                        { isExpanded && <div className=" border-0 mb-2 position-relative">
                            <div className=" p-0">
                                <Dropdown
                                    trigger={["click"]}
                                    overlay={
                                        <ProfileMenu
                                            user={user}
                                            setData={setData}
                                            onLogout={handleLogout}
                                        />
                                    }
                                    placement="bottomRight"
                                >
                                    <div className="menu-content sidebar-user-profile d-flex flex-nowrap align-items-center cursor-pointer">
                                        <div className="symbol symbol-circle symbol-40px me-3 flex-shrink-0">
                                            <img alt="Logo" src={photo || "../assets/media/avatars/blank.png"}/>
                                        </div>

                                        {isExpanded && (
                                            <div className="sidebar-user-text d-flex flex-column flex-grow-1">
                                                <div className="fw-bold fs-6 sidebar-user-name text-truncate">
                                                    {seller_name || name}
                                                </div>
                                                <div className="fw-semibold text-muted fs-8 text-truncate">
                                                    {email}
                                                </div>
                                            </div>
                                        )}

                                        <div className="sidebar-user-actions flex-shrink-0 ms-2">
                                            <i className="ki-outline ki-down fs-3 sidebar-user-chevron"/>
                                        </div>
                                    </div>
                                </Dropdown>
                           
                                {/* {user?.admin && (
                                    <>
                                <div className="mt-3 d-flex justify-content-center align-items-center p-0 border-top border-bottom">
                                    <button 
                                        onClick={() => setIsDrpListCollapsed(!isDrpListCollapsed)}
                                        className="btn btn-sm btn-light w-100"
                                    >
                                        <i className={`ki-outline ${isDrpListCollapsed ? 'ki-eye' : 'ki-eye-slash'} fs-3 me-1`}></i>
                                        {isDrpListCollapsed ? 'Show Seller Accounts' : 'Hide Seller Accounts'}
                                    </button>
                                </div>

                                {!isDrpListCollapsed &&(
                                <div className="drp-list">
                                    <DropdownUserList userData={user} setData={setData} />
                                    </div>
                                )}
                                </>
                            )} */}
                            </div>
                        </div>}
                    </div>

                    {isExpanded && <div className="separator border-gray-300 mb-7 mx-10"/> }

                    {/* Sidebar Menu */}
                    <div className="app-sidebar-menu-primary menu menu-column menu-rounded">
                        <MenuView className={collapsed && !isMobile ? "collapsed" : ""}>
                            <Menu
                                selectedKeys={
                                    collapsed && !isMobile
                                        ? [current, current.split("/")[0]]
                                        : [current]
                                }
                                openKeys={openKeys}
                                onOpenChange={setOpenKeys}
                                onClick={(e) => {
                                    const isParentItem = menuItems?.some(
                                        (item) => item?.key === e.key && item?.children?.length > 0
                                    );

                                    if (isParentItem && collapsed && !isMobile) {
                                        // Expand sidebar first, then open the submenu
                                        toggleSidebar();
                                        setOpenKeys([e.key]);
                                        return;
                                    }

                                    setCurrent(e.key);
                                    if (isMobile) toggleSidebar(); // Auto-close on mobile after selection
                                }}
                                theme={themeMode === "dark" ? "dark" : "light"}
                                mode="inline"
                                items={menuItems}
                                inlineCollapsed={!isMobile && collapsed}
                                style={{ border: "none" }}
                            />
                        </MenuView>
                    </div>
                </div>
            </div>

            {/* Sidebar Footer */}
            <div className="app-sidebar-footer d-flex flex-column px-6 pb-8" id="kt_app_sidebar_footer">

                <div className="d-flex mb-4">
                    <Tooltip
                        title={!isExpanded ? (isDark ? "Light mode" : "Dark mode") : ""}
                        placement="right"
                    >
                        <button
                            type="button"
                            onClick={toggleTheme}
                            className={`btn btn-light d-flex align-items-center justify-content-center ${!isExpanded ? "btn-icon w-40px h-40px" : "w-100 py-3"}`}
                        >
                            <i className={`ki-outline ${isDark ? "ki-sun" : "ki-moon"} ${!isExpanded ? "fs-2" : "fs-4 me-2"}`} />
                            {isExpanded && (
                                <span className="fw-bold">{isDark ? "Switch To Light Mode" : "Switch To Dark Mode"}</span>
                            )}
                        </button>
                    </Tooltip>
                </div>

                <div className="d-flex mb-4">
                    <Tooltip title={!isExpanded ? "Report an issue" : ""} placement="right">
                        <button
                            onClick={() => setReportIssueOpen(true)}
                            className={`btn btn-light-danger d-flex align-items-center justify-content-center ${!isExpanded ? "btn-icon w-40px h-40px" : "w-100 py-3"}`}
                            type="button"
                        >
                            <i className={`ki-outline ki-information-5 ${!isExpanded ? "fs-2" : "fs-4 me-2"}`} />
                            {isExpanded && <span className="fw-bold">Report an issue</span>}
                        </button>
                    </Tooltip>
                </div>
                {showAdminButton && (
                    <div className="d-flex mb-4">
                        <button
                            onClick={() => {
                                setData(user?.admin);
                                navigate(findRoutes?.[user?.admin?.loginType]);
                            }}
                            className={`btn btn-danger d-flex align-items-center justify-content-center ${!isExpanded ? "btn-icon w-40px h-40px" : "w-100 py-3"}`}
                            title={!isExpanded ? "Go to admin panel" : ""}
                        >
                            <i className={`ki-outline ki-arrow-left ${!isExpanded ? "fs-2" : "fs-4 me-2"}`} />
                            {isExpanded && (
                                <span className="fw-bold">
                                    Go to admin panel
                                </span>
                            )}
                       
                        </button>
                    </div>
                )}

                {isExpanded && <div className="separator border-gray-300 mb-4" />}

                <div className="d-flex align-items-center" style={{ overflow: "hidden" }}>
                    <div
                        className="d-flex justify-content-center align-items-center rounded-circle flex-shrink-0"
                        style={{
                            width: "30px",
                            height: "30px",
                            backgroundColor: "#f45b68",
                            marginRight: isExpanded ? "12px" : "0",
                            transition: "margin 0.3s ease",
                        }}
                    >
                        <i className="ki-outline ki-time fs-5 text-white" />
                    </div>

                    {isExpanded && (
                        <div className="d-flex flex-column" style={{ whiteSpace: "nowrap" }}>
                            <span className="text-muted fw-bold fs-9 text-uppercase">System Time</span>
                            <span
                                className="fw-semibold text-danger fs-8"
                                style={{ fontFamily: "'Inter', sans-serif" }}
                            >
                                {currentTime}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            <ReportIssueModal
                open={reportIssueOpen}
                onClose={() => setReportIssueOpen(false)}
                formUrl={asanaIssueFormUrl}
            />
        </div>
    );
}